import React, { useState } from 'react'
import { Card } from 'react-bootstrap'
import { codeToTitle, getFormattedDateTime, parseJwt } from '../../../../core/helper'
import { KTCard } from '../../../../../../../general/helpers'
import { useIntl } from 'react-intl'
import Modal from '../../../../components/Modal'
import { keysforDateFormatValue } from '../../../../core/globals'
import { useAuth } from '../../../../../auth'

interface DetailSectionI {
    details: any
    title: string
    fields: string[]
    labels: { [name: string]: string }
    detailsLoader: boolean | undefined
}

export default function DetailSection({
    details,
    title,
    fields,
    labels,
    detailsLoader,
    ..._props
}: DetailSectionI) {
    const intl = useIntl()
    const { auth } = useAuth()

    const { activeRetailer } = parseJwt(auth?.accessToken ?? '')
    const [isModalOpen, setIsModalOpen] = useState(false)

    function getValueByPath(obj, path) {
        if (path?.includes('.')) {
            const keys = path?.split('.')
            let value = obj

            for (const key of keys) {
                if (value?.hasOwnProperty(key)) {
                    value = value?.[key] ?? 'N/A'
                } else {
                    return undefined
                }
            }

            return value ?? 'N/A'
        } else {
            return obj?.[path] ?? 'N/A'
        }
    }

    const fetchFieldValue = (fieldVal: string, index?: string) => {
        // For local mapping, if label key is same as field name
        const path = Object.keys(labels).find((item) => item === fieldVal) || fieldVal
        let result = getValueByPath(details, index ? path?.replace('.0.', `.${index}.`) : path)

        // Check key presence in date format value list
        let isDate = false;
        if (keysforDateFormatValue.payment) {
            // This check might need adjustment if payment schemes use different keys structure
            // For now, assuming standard date fields or if path is found in globals
            if (fieldVal.toLowerCase().includes('date') || fieldVal.toLowerCase().includes('time')) {
                isDate = true;
            }
        }

        if (result && result !== 'N/A' && (isDate || (path && keysforDateFormatValue.payment.includes(path)))) {
            // Simple check if string looks like date if not in keys list, but let's stick to keys first
            // If "startDateTime" or "endDateTime"
            if (typeof result === 'string' && (result.includes('T') || result.includes('-'))) {
                result = getFormattedDateTime(
                    activeRetailer?.locale,
                    activeRetailer?.timeZone,
                    !result.endsWith('Z') ? result + 'Z' : result
                )
            }
        }

        if (typeof result === 'boolean') {
            return result ? 'Yes' : 'No' // Or translated variants
        }

        return result !== undefined && result.toString()
    }

    const fetchLabel = (fieldVal: string) => {
        return labels[fieldVal] ?? fieldVal
    }

    const getTabFragment = (index?: string) => (
        <div className='table-responsive'>
            <table
                id='kt_table_users'
                className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
            >
                <tbody className='text-gray-600 fw-bold'>
                    {fields.map((field) => {
                        // If label exists in labels map, use it to get translation ID, else fallback
                        const labelKey = fetchLabel(field);
                        const label = labelKey
                            ? intl.formatMessage({ id: labelKey, defaultMessage: labelKey })
                            : ''
                        const value = fetchFieldValue(field, index)

                        return (
                            <tr key={field}>
                                <td className='fw-bold min-w-120px'>{label}</td>
                                <td className='text-end mw-300px'>
                                    {field === 'errorMessage' ? (
                                        <button
                                            className='btn btn-link text-primary'
                                            onClick={() => {
                                                setIsModalOpen(true)
                                            }}
                                        >
                                            {intl.formatMessage({ id: 'VIEW' })}
                                        </button>
                                    ) : (
                                        value ?? 'N/A'
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )

    return (
        <>
            {' '}
            <KTCard rounded='rounded' border shadow>
                <Card.Header>
                    <Card.Title>{codeToTitle(title)}</Card.Title>
                </Card.Header>
                <Card.Body>
                    {detailsLoader !== undefined && detailsLoader === false ? (
                        getTabFragment()
                    ) : (
                        <div className='d-flex justify-content-center align-items-center'>
                            <span className='spinner-border spinner-border-md align-middle text-primary' />
                        </div>
                    )}
                </Card.Body>
            </KTCard>
            {isModalOpen && (
                <Modal
                    heading={intl.formatMessage({ id: 'ERROR.MESSAGE' })}
                    onClose={() => {
                        setIsModalOpen(false)
                    }}
                >
                    <div className='fs-6'>{fetchFieldValue('errorMessage') ?? 'N/A'}</div>
                </Modal>
            )}
        </>
    )
}
